import React from 'react';
import PropTypes from 'prop-types';
import HotelIcon from '@material-ui/icons/Hotel';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import BathtubIcon from '@material-ui/icons/Bathtub';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ActionArea from '../ActionArea';
import Box from '../Box';
import Divider from '../Divider';
import Heading from '../Heading';
import Media from '../Media';
import Paper from '../Paper';
import Skeleton from '../Skeleton';
import Text from '../Text';
import { StyledContainer, StyledImg, StyledDetails, StyledIcon } from './Styles';
import { implode, isEmpty } from '../../utils/helpers';
function PropertyCard(_ref) {
  var bedrooms = _ref.bedrooms,
    bathrooms = _ref.bathrooms,
    elevation = _ref.elevation,
    footerContent = _ref.footerContent,
    onView = _ref.onView,
    photo = _ref.photo,
    parkingSpaces = _ref.parkingSpaces,
    rentAmount = _ref.rentAmount,
    rentFrequency = _ref.rentFrequency,
    skeleton = _ref.skeleton,
    unit = _ref.unit,
    streetNumber = _ref.streetNumber,
    streetName = _ref.streetName,
    suburb = _ref.suburb,
    city = _ref.city;
  var isMobile = useMediaQuery(function (theme) {
    return theme.breakpoints.down('sm');
  });
  var abbreviatedRentFrequency = function abbreviatedRentFrequency() {
    switch (rentFrequency) {
      case 'weekly':
        return 'pw';
      case 'monthly':
        return 'pm';
      case 'daily':
        return 'day';
      case 'fortnightly':
        return 'fortn';
      default:
        return 'pw';
    }
  };
  var address1 = implode(' ', [implode('/', [unit, streetNumber]), streetName]);
  var address2 = implode(', ', [suburb, city]);
  var address = address1 + " " + address2;
  return /*#__PURE__*/React.createElement(Paper, {
    rounded: true,
    elevate: elevation,
    fill: !isMobile
  }, /*#__PURE__*/React.createElement(ActionArea, {
    onClick: onView
  }, /*#__PURE__*/React.createElement(StyledContainer, null, /*#__PURE__*/React.createElement(StyledImg, null, /*#__PURE__*/React.createElement(Media, {
    skeleton: skeleton,
    src: photo,
    title: address
  })), /*#__PURE__*/React.createElement(StyledDetails, null, !skeleton && /*#__PURE__*/React.createElement(Heading, {
    as: "h3",
    gutterBottom: false
  }, address1), skeleton && /*#__PURE__*/React.createElement(Skeleton, {
    width: "75%",
    height: 30
  }), !skeleton && /*#__PURE__*/React.createElement(Text, {
    variant: "subtitle1",
    weight: "500",
    color: "textPrimary"
  }, address2), skeleton && /*#__PURE__*/React.createElement(Skeleton, {
    width: "65%",
    height: 30
  }), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    width: "100%",
    align: "center",
    marginBottom: !skeleton ? '20px' : '-13px'
  }, !skeleton && !isEmpty(rentAmount) && /*#__PURE__*/React.createElement(Text, {
    as: "span",
    weight: "600",
    variant: "inherit",
    color: "textPrimary"
  }, "$", rentAmount, abbreviatedRentFrequency()), skeleton && /*#__PURE__*/React.createElement(Skeleton, {
    width: 60,
    height: 27
  }), /*#__PURE__*/React.createElement(StyledIcon, null, !skeleton && !isEmpty(bedrooms) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    as: "span",
    weight: "600",
    variant: "inherit",
    color: "textPrimary"
  }, bedrooms), "\xA0", /*#__PURE__*/React.createElement(HotelIcon, null)), skeleton && /*#__PURE__*/React.createElement(Skeleton, {
    width: 40,
    height: 27
  })), /*#__PURE__*/React.createElement(StyledIcon, null, !skeleton && !isEmpty(bathrooms) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    as: "span",
    weight: "600",
    variant: "inherit",
    color: "textPrimary"
  }, bathrooms), "\xA0", /*#__PURE__*/React.createElement(BathtubIcon, null)), skeleton && /*#__PURE__*/React.createElement(Skeleton, {
    width: 40,
    height: 27
  })), /*#__PURE__*/React.createElement(StyledIcon, null, !skeleton && !isEmpty(parkingSpaces) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    as: "span",
    weight: "600",
    variant: "inherit",
    color: "textPrimary"
  }, parkingSpaces), "\xA0", /*#__PURE__*/React.createElement(DirectionsCarIcon, null)), skeleton && /*#__PURE__*/React.createElement(Skeleton, {
    width: 40,
    height: 27
  }))), footerContent && /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    justify: "flex-end",
    align: "center"
  }, footerContent)))));
}
PropertyCard.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * URL for the property photo
   */
  photo: PropTypes.string,
  /**
   * The number of bedrooms
   */
  bedrooms: PropTypes.number,
  /**
   * The number of bathrooms
   */
  bathrooms: PropTypes.number,
  /**
   * Shadow depth
   */
  elevation: PropTypes.bool,
  /**
   * Content to display in footer, action buttons etc.
   */
  footerContent: PropTypes.node,
  /**
   * View click handler
   */
  onView: PropTypes.oneOfType([PropTypes.func, PropTypes.instanceOf(null)]),
  /**
   * The number of vehicles that can be parked
   */
  parkingSpaces: PropTypes.number,
  /**
   * Dollar amount for rent
   */
  rentAmount: PropTypes.number,
  /**
   * Dollar amount for rent
   */
  rentFrequency: PropTypes.oneOf(['weekly', 'monthly', 'fortnightly', 'daily', '']),
  /**
   * Show skeleton layout
   */
  skeleton: PropTypes.bool,
  /**
   * Unit number in address
   */
  unit: PropTypes.string,
  /**
   * Street number in address
   */
  streetNumber: PropTypes.string,
  /**
   * Street name in address
   */
  streetName: PropTypes.string,
  /**
   * Address suburb
   */
  suburb: PropTypes.string,
  /**
   * Address city
   */
  city: PropTypes.string
} : {};
PropertyCard.defaultProps = {
  photo: '',
  bedrooms: 0,
  bathrooms: 0,
  elevation: true,
  footerContent: undefined,
  parkingSpaces: 0,
  onView: null,
  rentAmount: 0,
  rentFrequency: 'weekly',
  skeleton: false,
  unit: '',
  streetNumber: '',
  streetName: '',
  suburb: '',
  city: ''
};
export default PropertyCard;